import { action } from '@ember/object';
import Component from '@glimmer/component';
import dateUtils, { dateToUtc } from 'donor-elf-web/lib/date-utils';
import type {
  IntervalCumulativeAmount,
  IntervalDate,
} from 'donor-elf-web/lib/journal-utils';
import Highcharts from 'highcharts';
import moment from 'moment';

interface ComponentArgs {
  journal: any;
  journalGoal: any;
  intervalDates: IntervalDate[];
  intervalCumulativeAmounts: IntervalCumulativeAmount[];
  chartHeight: number;
}

export default class JournalRaisedChartComponent extends Component<ComponentArgs> {
  @action
  renderChart(animation: boolean, element: HTMLElement) {
    Highcharts.chart(element, this.getChartSettings({ animation }));
  }

  get journalGoalDateUtc() {
    return dateToUtc(this.args.journal.goalDate);
  }

  get seriesStartUtc() {
    let firstIntervalStartDate = dateUtils.deserializeDateOnly(
      this.args.intervalDates[0].start,
    );
    let date = moment(firstIntervalStartDate).add({ days: -1 }).toDate();
    return dateToUtc(date);
  }

  get totalDecisionAmount() {
    return (
      this.args.intervalCumulativeAmounts[
        this.args.intervalCumulativeAmounts.length - 1
      ]?.decision || 0
    );
  }

  get goalSeries() {
    let {
      journalGoalDateUtc,
      seriesStartUtc,
      args: { journalGoal },
    } = this;

    return {
      name: 'Goal',
      type: 'line',
      color: '#f0ad4e',
      lineWidth: 4,
      marker: {
        enabled: false,
      },
      data: [
        [seriesStartUtc, journalGoal.amount],
        [journalGoalDateUtc, journalGoal.amount],
      ],
    };
  }

  get onTrackSeries() {
    let {
      seriesStartUtc,
      journalGoalDateUtc,
      args: { journalGoal },
    } = this;
    return {
      name: 'On Track Goal',
      type: 'line',
      color: '#eceeef',
      marker: {
        enabled: false,
      },
      data: [
        [seriesStartUtc, 0],
        [journalGoalDateUtc, journalGoal.amount],
      ],
    };
  }

  get decisionsSeries() {
    let {
      seriesStartUtc,
      args: {
        intervalCumulativeAmounts,
        journal: { decisionsRequireGift },
      },
    } = this;

    return {
      name: 'All Decisions',
      type: decisionsRequireGift ? 'line' : 'area',
      color: '#8ac7ec',
      marker: {
        enabled: false,
      },
      data: [[seriesStartUtc, 0]].concat(
        intervalCumulativeAmounts.map((item) => {
          let date = dateUtils.deserializeDateOnly(item.end);
          let amount = item.decision;
          // There's an edge case where a decision has a pledge with a gift that
          // comes in before the decision date, and that makes the decisionAmount less than the
          // the withGift amount. So we're preventing that here
          if (item.decision < item.withGift) {
            amount = item.withGift;
          }
          return [dateToUtc(date), amount];
        }),
      ),
    };
  }

  get withGiftSeries() {
    let {
      seriesStartUtc,
      args: { intervalCumulativeAmounts },
    } = this;

    return {
      name: 'Decisions with Gifts',
      type: 'area',
      color: '#0b68a1',
      marker: {
        enabled: false,
      },
      data: [[seriesStartUtc, 0]].concat(
        intervalCumulativeAmounts.map((item) => {
          let date = dateUtils.deserializeDateOnly(item.end);
          return [dateToUtc(date), item.withGift];
        }),
      ),
    };
  }

  getChartSettings({ animation }: { animation: boolean }) {
    let {
      totalDecisionAmount,
      args: { journalGoal, chartHeight },
    } = this;

    let series = [];
    series.push(this.decisionsSeries);
    series.push(this.withGiftSeries);
    series.push(this.onTrackSeries);
    series.push(this.goalSeries);

    return {
      chart: {
        height: chartHeight,
        margin: [2, 0, 2, 0],
        borderWidth: 0,
      },
      title: {
        text: undefined,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          // don't display the dummy year
          month: '%e. %b',
          year: '%b',
        },
        // tickInterval: 1000 * 60 * 60 * 24 * 7, // 1 week;
        // tickPositions: [
        //   Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
        // ].concat(intervalDates.map(dateInfo => {
        //   let date = dateUtils.deserializeDateOnly(dateInfo.end);
        //   return [Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())];
        // })),
        // tickPosition: 'inside',
        startOnTick: false,
      },
      yAxis: {
        min: 0,
        labels: {
          enabled: false,
        },
        max: Math.max(journalGoal.amount, totalDecisionAmount),
        title: {
          text: undefined,
        },
        startOnTick: false,
        endOnTick: false,
        tickPositions: [0],
      },
      plotOptions: {
        series: {
          animation: animation ?? true,
        },
      },
      legend: {
        enabled: false,
      },
      series,
    } as Highcharts.Options;
  }
}
